import { englishError } from "../utils/databaseErrors";
import React, { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";
import { availableQuantity } from "../utils/stock";
import { validMarbleQuantity } from "../utils/marbleQuantity";

function BlockTransferTab() {
  const [blocks, setBlocks] = useState([]);
  const [showrooms, setShowrooms] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);

  const [selectedBlockId, setSelectedBlockId] = useState("");
  const [sourceDistributionId, setSourceDistributionId] = useState("");
  const [targetShowroomId, setTargetShowroomId] = useState("");
  const [transferSlabsCount, setTransferSlabsCount] = useState("");
  const [reservations, setReservations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAll();
  }, []);

  async function fetchAll() {
    const [blocksData, showroomsData, distributionsData, materialsData, suppliersData, customersData, bookingsData] =
      await Promise.all([
        supabase.from("block").select("*"),
        supabase.from("showroom").select("*"),
        supabase.from("block_distribution").select("*"),
        supabase.from("material").select("*"),
        supabase.from("supplier").select("*"),
        supabase.from("customer").select("*"),
        supabase.from("reservation").select("*"),
      ]);

    setBlocks(blocksData.data || []);
    setShowrooms(showroomsData.data || []);
    setDistributions(distributionsData.data || []);
    setMaterials(materialsData.data || []);
    setSuppliers(suppliersData.data || []);
    setCustomers(customersData.data || []);
    setReservations(bookingsData.data || []);
    setError(bookingsData.error || distributionsData.error ? "Unable to load stock or reservations." : "");
  }

  // ماب لعرض الأسماء
  const materialById = Object.fromEntries(materials.map((m) => [m.id, m.name]));
  const supplierById = Object.fromEntries(suppliers.map((s) => [s.id, s.name]));
  const customerById = Object.fromEntries(customers.map((c) => [c.id, c.name]));

  // فلترة التوزيعات الخاصة بالبلوك المحدد
  const filteredDistributions = distributions.filter(
    (d) => String(d.block_id) === String(selectedBlockId)
  );

  // الشرائح المتوفرة في المعرض المصدر
  const sourceDistribution = filteredDistributions.find(
    (d) => String(d.id) === String(sourceDistributionId)
  );
  const sourceShowroomId = sourceDistribution?.showroom_id;
  const availableSlabsInSource = sourceDistribution
    ? availableQuantity(sourceDistribution, reservations)
    : 0;

  // تحقق قبل النقل
  function canTransfer() {
    if (!selectedBlockId || !sourceShowroomId || !targetShowroomId) return false;
    if (String(sourceShowroomId) === String(targetShowroomId)) return false;
    const slabsNum = Number(transferSlabsCount);
    if (!validMarbleQuantity(slabsNum) || busy || error) return false;
    if (slabsNum > availableSlabsInSource) return false;
    return true;
  }

  async function handleTransfer(e) {
    e.preventDefault();
    if (!canTransfer()) {
      alert("Check the required fields.");
      return;
    }

    const slabsNum = Number(transferSlabsCount);

    setBusy(true);
    try {
      const { error: transferError } = await supabase.rpc("transfer_marble_stock_tx", {
        p_source_distribution_id: Number(sourceDistributionId),
        p_target_showroom_id: Number(targetShowroomId), p_quantity: slabsNum,
      });
      if (transferError) throw transferError;
      alert("Stock transferred successfully!");
      await fetchAll();
      setTransferSlabsCount(""); setSelectedBlockId("");
      setSourceDistributionId(""); setTargetShowroomId("");
    } catch (err) { alert("Unable to transfer stock: " + englishError(err.message)); }
    finally { setBusy(false); }
  }

  return (
    <div className="ui-legacy-page" dir="ltr" style={{ maxWidth: 900, margin: "auto", padding: 20 }}>
      <h2 style={{ color: "var(--accent, #514536)", marginBottom: 20 }}>
        Transfer stock between showrooms
      </h2>
      {error && <p role="alert" style={{ color: "crimson" }}>{error}</p>}
      <button className="ui-secondary" type="button" onClick={fetchAll} disabled={busy}>Refresh inventory</button>

      <form className="ui-form"
        onSubmit={handleTransfer}
        style={{ display: "flex", flexDirection: "column", gap: 16 }}
      >
        <label>
          Select a block:
          <select
            value={selectedBlockId}
            onChange={(e) => {
              setSelectedBlockId(e.target.value);
              setSourceDistributionId("");
              setTargetShowroomId("");
              setTransferSlabsCount("");
            }}
            required
          >
            <option value="">-- Select a block --</option>
            {blocks.map((b) => {
              const matName = materialById[b.material_id] || "";
              const supplierName =
                supplierById[b.supplier_id] ||
                customerById[b.customer_id] ||
                "";
              return (
                <option key={b.id} value={b.id}>
                  {`Code: ${b.code} — ${matName}${
                    supplierName ? ` — Supplier: ${supplierName}` : ""
                  }`}
                </option>
              );
            })}
          </select>
        </label>

        {selectedBlockId && (
          <>
            <div>
              <b>Current showroom block distributions:</b>
              <ul>
                {filteredDistributions.length === 0 && (
                  <li>This block has no showroom distribution.</li>
                )}
                {filteredDistributions.map((d) => {
                  const showroom = showrooms.find((s) => s.id === d.showroom_id);
                  return (
                    <li key={d.id}>
                      {showroom ? showroom.name : `Showroom ${d.showroom_id}`} -
                      Slab count: {d.slabs_count}
                    </li>
                  );
                })}
              </ul>
            </div>

            <label>
              Select the source showroom:
              <select
                value={sourceDistributionId}
                onChange={(e) => {
                  setSourceDistributionId(e.target.value);
                  setTransferSlabsCount("");
                }}
                required
              >
                <option value="">-- Select the source showroom --</option>
                {filteredDistributions.map((d) => {
                  const showroom = showrooms.find((s) => s.id === d.showroom_id);
                  return (
                    <option key={d.id} value={d.id}>
                      {showroom ? showroom.name : `Showroom ${d.showroom_id}`} (
                      {availableQuantity(d, reservations)} pieces available) · {d.variant_suffix || `${d.face_treatment || "none"}/${d.back_treatment || "none"}${d.is_polished ? " · Polished" : ""}`}
                    </option>
                  );
                })}
              </select>
            </label>

            <label>
              Select the destination showroom:
              <select
                value={targetShowroomId}
                onChange={(e) => setTargetShowroomId(e.target.value)}
                required
              >
                <option value="">-- Select the destination showroom --</option>
                {showrooms
                  .filter((s) => String(s.id) !== String(sourceShowroomId))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
              </select>
            </label>

            <label>
              Slab count (Available: {availableSlabsInSource}):
              <input
                type="number"
                min={0.001}
                step="0.001"
                max={availableSlabsInSource}
                value={transferSlabsCount}
                onChange={(e) => setTransferSlabsCount(e.target.value)}
                required
              />
            </label>

            <button className="ui-secondary"
              type="submit"
              disabled={!canTransfer()}
              style={{
                backgroundColor: canTransfer() ? "var(--accent, #514536)" : "#d2cbc1",
                color: "#f2ede4",
                padding: "10px 20px",
                borderRadius: 8,
                fontWeight: "bold",
                cursor: canTransfer() ? "pointer" : "not-allowed",
                marginTop: 12,
              }}
            >
              Transfer stock
            </button>
          </>
        )}
      </form>
    </div>
  );
}

export default BlockTransferTab;
